import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import OutlineClose from 'Components/Atoms/OutlineIcons/OutlineClose';
import OutlineArrowLeft from 'Components/Atoms/OutlineIcons/OutlineArrowLeft';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { trackError } from 'Utils/errorMonitoring';
import { Slot } from 'nano-slots';
import DrawerWithPreview from './DrawerWithPreview';
import React, { useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import DrawerStackItemContext from './DrawerStackItemContext';

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        if (this.props.onError) {
            this.props.onError(error, info);
        } else {
            console.error('Error capturado por ErrorBoundary:', error, info);
        }
    }

    render() {
        if (this.state.hasError) {
            // Renderizar un fallback o null si hay error
            return this.props.fallback || null;
        }

        return this.props.children;
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node,
    fallback: PropTypes.node,
    onError: PropTypes.func,
};

const variants = {
    incoming: (direction) => ({
        x: direction > 0 ? 100 : -100,
        opacity: 0,
        position: 'absolute',
        inset: 0,
    }),
    active: { x: 0, scale: 1, opacity: 1, position: 'relative' },
    exit: (direction) => ({
        x: direction > 0 ? -100 : 100,
        opacity: 0.2,
        display: 'none',
        position: 'absolute',
        inset: 0,
    }),
};

const transition = {
    duration: 0.15,
};

function getDrawerProps(drawer) {
    if (!drawer) return {};
    return {
        ...drawer.component.drawerStackProps,
        ...drawer.props.drawerStackProps,
    };
}

export default function DrawerStack(props) {
    const { direction, index, drawers, onBack, onClose } = props;
    const isFetching = useIsFetching();

    const open = drawers.length > 0;
    const hasPrevious = index > 0;
    const currentDrawer = drawers[index];
    const { showUnknownError } = useErrors();
    const { enqueueSnackbar } = useSnackbar();
    const [shouldCheckFocus, setShouldCheckFocus] = useState(false);
    const wrapRef = useRef(null);
    const drawerRef = useRef(null);

    useEffect(() => {
        if (shouldCheckFocus && isFetching === 0 && wrapRef.current) {
            const inputs = wrapRef.current.querySelectorAll('[data-autofocus="true"]');
            const lastInput = inputs[inputs.length - 1];

            if (lastInput) {
                const isVisible = lastInput.offsetParent !== null;
                if (isVisible) {
                    lastInput.focus();
                }
            }
            setShouldCheckFocus(false);
        }
    }, [shouldCheckFocus, isFetching]);

    const handleClose = () => {
        if (currentDrawer && currentDrawer.props.onClose) {
            currentDrawer.props.onClose();
        }
        onClose();
    };

    const handleBack = () => {
        if (currentDrawer && currentDrawer.props.onClose) {
            currentDrawer.props.onClose();
        }
        onBack();
    };

    const handleSubmit = async (data) => {
        if (currentDrawer && currentDrawer.props.onSubmit) {
            await currentDrawer.props.onSubmit(data);
        }
        const actions = hasPrevious ? onBack : onClose;
        actions();
    };

    const handleError = (error, { title, description } = {}) => {
        if (title) {
            trackError(error);
            enqueueSnackbar(title, { variant: 'error', description });
        } else {
            showUnknownError(error);
        }
        onClose();
    };

    const title = currentDrawer?.props?.title ?? '';
    const titleImage = currentDrawer?.props?.titleImage ?? null;
    const drawerProps = getDrawerProps(currentDrawer);

    return (
        <DrawerWithPreview
            currentId={currentDrawer?.id}
            open={open}
            onClose={handleClose}
            ref={drawerRef}
            {...drawerProps}
        >
            <div className="grid grid-rows-[auto_1fr] min-h-full">
                <div
                    className="sticky top-0 z-20 bg-white"
                    style={{
                        boxShadow:
                            '0 4px 6px -1px rgb(0 0 0 / min(0.001 * var(--scroll-y), 0.1)), 0 2px 4px -2px rgb(0 0 0 / min(0.001 * var(--scroll-y), 0.1))',
                    }}
                >
                    <div className="flex items-center py-2 px-4 md:px-6">
                        {hasPrevious && (
                            <IconButton
                                variant="ghost"
                                icon={<OutlineArrowLeft />}
                                className="shrink-0"
                                onClick={handleBack}
                            />
                        )}
                        <div className="flex justify-between items-center gap-2 grow">
                            <div className="text-lg font-semibold grow flex items-center gap-2">
                                {titleImage && (
                                    <img
                                        src={titleImage}
                                        alt=""
                                        width={24}
                                        height={24}
                                        className="size-6 shrink-0 object-contain"
                                    />
                                )}
                                <span>{title}</span>
                                <Slot name="drawer-stack-title-badge" />
                            </div>
                            <div className="empty:hidden shrink-0">
                                <Slot name="drawer-stack-header" />
                            </div>
                        </div>
                        <IconButton
                            variant="ghost"
                            icon={<OutlineClose />}
                            className="shrink-0"
                            onClick={handleClose}
                        />
                    </div>
                    <div className="md:hidden">
                        <Slot name="drawer-stack-preview-mobile" />
                    </div>
                </div>
                <div className="px-4 md:px-6 overflow-hidden">
                    <div className="relative h-full" ref={wrapRef}>
                        {drawers.map((drawer, i) => (
                            <motion.div
                                key={drawer.id}
                                custom={direction}
                                variants={variants}
                                transition={transition}
                                initial="incoming"
                                animate={i === index ? 'active' : 'exit'}
                                className="h-full"
                                onAnimationComplete={(name) => {
                                    if (name === 'active') {
                                        setShouldCheckFocus(true);
                                    }
                                }}
                            >
                                <ErrorBoundary
                                    onError={(error) => {
                                        showUnknownError(error);
                                        onClose();
                                    }}
                                >
                                    <DrawerStackItemContext.Provider value={{ isActive: i === index }}>
                                        <drawer.component
                                            {...drawer.props}
                                            onClose={handleClose}
                                            onSubmit={handleSubmit}
                                            onError={handleError}
                                            onCancel={() => {
                                                if (typeof drawer.props.onCancel === 'function') {
                                                    drawer.props.onCancel();
                                                }

                                                const actions = hasPrevious ? onBack : onClose;
                                                actions();
                                            }}
                                        />
                                    </DrawerStackItemContext.Provider>
                                </ErrorBoundary>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </DrawerWithPreview>
    );
}

DrawerStack.propTypes = {
    direction: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
    drawers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            component: PropTypes.elementType.isRequired,
            drawerStackProps: PropTypes.object,
            props: PropTypes.object,
        }),
    ).isRequired,
    onBack: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
